/**
 * 🎭 EmotionDailySummaryCard — Feature Premium (Emotion Analytics)
 *
 * Card-resumo com as médias por hora de cada comportamento (feliz, triste,
 * raiva, neutro) e o "sentiment score" geral do ambiente (-1 a +1).
 *
 * Só é renderizado no dashboard quando a organização tem a feature
 * habilitada. Sem flag → nada muda no sistema.
 *
 * Zero `any`.
 */

import React from 'react';
import { Smile, Frown, Angry, Meh, Gauge } from 'lucide-react';
import { EmotionStats } from '../../domain/entities/Analytics';

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface EmotionDailySummaryCardProps {
  data: EmotionStats;
}

// ─── Componente principal ──────────────────────────────────────────────────────

export const EmotionDailySummaryCard: React.FC<EmotionDailySummaryCardProps> = ({
  data,
}) => {
  const avg = data.daily_average;
  const score = avg.sentiment_score;

  const scoreLabel =
    score >= 0.25 ? 'Clima positivo' :
    score <= -0.25 ? 'Clima negativo' : 'Clima neutro';

  const scoreBadge =
    score >= 0.25
      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
      : score <= -0.25
        ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';

  const statItems = [
    {
      icon: <Smile className="w-5 h-5 text-green-500" />,
      label: 'Felizes (média/h)',
      value: avg.happy_avg_per_hour,
      color: 'text-green-600 dark:text-green-400',
    },
    {
      icon: <Meh className="w-5 h-5 text-gray-400" />,
      label: 'Neutros (média/h)',
      value: avg.neutral_avg_per_hour,
      color: 'text-gray-600 dark:text-gray-300',
    },
    {
      icon: <Frown className="w-5 h-5 text-blue-500" />,
      label: 'Tristes (média/h)',
      value: avg.sad_avg_per_hour,
      color: 'text-blue-600 dark:text-blue-400',
    },
    {
      icon: <Angry className="w-5 h-5 text-red-500" />,
      label: 'Com raiva (média/h)',
      value: avg.angry_avg_per_hour,
      color: 'text-red-600 dark:text-red-400',
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          Média do Dia
        </h3>
        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 font-medium uppercase tracking-wide">
          Premium
        </span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Média de pessoas por hora em cada comportamento
        {avg.hours_with_data > 0 && ` (${avg.hours_with_data} horas com dados)`}
      </p>

      {/* Grid de médias */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {statItems.map((item) => (
          <div
            key={item.label}
            className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50"
          >
            {item.icon}
            <p className={`text-2xl font-bold ${item.color}`}>
              {item.value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}
            </p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 text-center leading-tight">
              {item.label}
            </p>
          </div>
        ))}
      </div>

      {/* Sentiment score */}
      <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-violet-500" />
          <div>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              Sentimento geral
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              Índice de -1 (negativo) a +1 (positivo)
            </p>
          </div>
        </div>
        <div className="text-right">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-sm font-bold ${scoreBadge}`}>
            {score.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{scoreLabel}</p>
        </div>
      </div>
    </div>
  );
};
